import React, { useState } from 'react';
import { ShieldCheck, MapPin, Share2, Copy, Gift, Users, Trophy, ChevronRight } from 'lucide-react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import Snackbar from '../../common/Snackbar';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { createReferralUser } from '../../../store/slices/usersSlice';
import type { RootState } from '../../../store';


const ReferralLandingPage = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const location = useLocation();



    const { adminProfile } = useAppSelector((state: RootState) => state.users);
    const { referralCode: authReferralCode } = useAppSelector((state: RootState) => state.auth);

    // Get code from URL OR from navigation state (dashboard) OR from Redux
    const urlReferralCode = searchParams.get('referral_code');
    const stateReferralCode = location.state?.adminReferralCode;
    const reduxReferralCode = adminProfile?.referral_code || authReferralCode;
    const referralCode = urlReferralCode || stateReferralCode || reduxReferralCode || 'XY1Z34'; // Default for preview if none found

    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
        show: false,
        message: '',
        type: 'success'
    });

    const [formData, setFormData] = useState({
        mobile: '',
        first_name: '',
        last_name: '',
        email: ''
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isCopied, setIsCopied] = useState(false);

    const validateField = (name: string, value: string) => {
        let error = '';
        if (name === 'first_name' || name === 'last_name') {
            if (value.trim().length > 0 && /\d/.test(value)) {
                error = 'No numbers allowed';
            }
        } else if (name === 'email' && value.trim().length > 0) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) error = 'Invalid email';
        } else if (name === 'mobile' && value.length > 0) {
            if (value.length !== 10) error = 'Exactly 10 digits';
        }
        return error;
    };

    const isFormValid = formData.first_name.trim().length > 0 &&
        !/\d/.test(formData.first_name) &&
        formData.last_name.trim().length > 0 &&
        !/\d/.test(formData.last_name) &&
        formData.mobile.length === 10 &&
        (formData.email.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email));

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let finalValue = value;

        if (name === 'first_name' || name === 'last_name') {
            finalValue = value.replace(/[0-9]/g, '');
        } else if (name === 'mobile') {
            finalValue = value.replace(/\D/g, '').slice(0, 10);
        }

        setFormData(prev => ({
            ...prev,
            [name]: finalValue
        }));

        if (errors[name]) {
            const newErrors = { ...errors };
            delete newErrors[name];
            setErrors(newErrors);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        if (error !== (errors[name] || '')) {
            setErrors(prev => {
                const newErrors = { ...prev };
                if (error) {
                    newErrors[name] = error;
                } else {
                    delete newErrors[name];
                }
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!referralCode || referralCode.trim() === '') {
            setSnackbar({
                show: true,
                type: 'error',
                message: 'Referral Code is missing. Please ensure you are using a valid referral link.'
            });
            return;
        }

        if (!isFormValid) return;

        setLoading(true);

        try {
            const payload = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                mobile: formData.mobile,
                email: formData.email,
                referred_by_code: referralCode,
                role: 'Investor',
                isabletorefer: false
            };

            await dispatch(createReferralUser(payload)).unwrap();

            setSnackbar({
                show: true,
                type: 'success',
                message: 'Registration successful'
            });
            setFormData({ mobile: '', first_name: '', last_name: '', email: '' });
        } catch (error: any) {
            const msg = typeof error === 'string' ? error : (error?.message || 'Failed to register. Please try again.');
            setSnackbar({
                show: true,
                type: 'error',
                message: msg.toLowerCase().includes('exist') ? 'A user with this mobile number already exists.' : msg
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        const url = `${window.location.origin}${window.location.pathname}?referral_code=${referralCode}`;
        navigator.clipboard.writeText(url);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <div className="min-h-full font-outfit text-slate-900 bg-[#F8FAFC]">
            {/* Navigation */}
            <nav className="z-[60] bg-slate-900/90 backdrop-blur-md border border-slate-800 px-4 md:px-6 py-4 w-full shadow-none">
                <div className="max-w-[1400px] mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <img src="/animalkart-logo.png" alt="AnimalKart Logo" className="h-8 md:h-10 w-auto rounded-xl" />
                        <span className="text-white font-black text-xl md:text-2xl tracking-tighter">AnimalKart</span>
                    </div>
                    <div className="flex items-center gap-2 md:gap-3 text-slate-400 text-[10px] md:text-sm font-bold">
                        <MapPin size={14} className="text-emerald-500" />
                        <span>Kurnool, AP</span>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="relative overflow-hidden w-full shadow-xl border border-slate-200 border-t-0">
                {/* Background Image with Overlay */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="/buffalo-family.jpg"
                        alt="Farm Background"
                        className="w-full h-full object-cover scale-105 animate-slow-zoom"
                    />
                </div>

                <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-12 md:py-20 lg:py-32 flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">
                    {/* Hero Text Content */}
                    <div className="flex-1 text-center lg:text-left animate-in fade-in slide-in-from-left-8 duration-1000">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-6 backdrop-blur-sm">
                            <Trophy size={14} />
                            Referral Program
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-7xl font-black text-white mb-6 leading-[1.05] tracking-tight">
                            Invite Friends, <br />
                            <span className="text-amber-400 drop-shadow-sm">Earn Coins</span>
                        </h1>
                        <p className="text-base md:text-lg lg:text-xl text-slate-300 font-medium max-w-xl mb-10 leading-relaxed">
                            Share your unique link with friends! For every friend who signs up and makes their first purchase, you'll earn coins credited directly to your account.
                        </p>

                        {/* Reference Image Style Referral Box */}
                        <div className="bg-[#f0f7ff] border border-blue-100 rounded-[1.25rem] p-4 md:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-2xl mx-auto lg:mx-0 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
                            <div className="flex items-center gap-4 md:gap-5">
                                <div className="p-2 md:p-3 bg-white rounded-xl shadow-sm border border-blue-50">
                                    <Share2 className="text-[#10b981] w-5 h-5 md:w-6 md:h-6" />
                                </div>
                                <div className="flex flex-col text-left">
                                    <span className="text-base md:text-lg lg:text-xl font-bold text-slate-800 leading-tight">Share Your Referral Link</span>
                                    <span className="text-[11px] md:text-[13px] font-medium text-slate-500 mt-0.5">
                                        Code: <span className="font-bold text-slate-900 uppercase tracking-wide">{referralCode}</span>
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 w-full sm:w-auto">
                                <button
                                    onClick={handleCopy}
                                    className="h-11 px-5 rounded-lg bg-white border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm"
                                >
                                    <Copy size={16} />
                                    {isCopied ? 'Copied' : 'Copy Link'}
                                </button>
                                <button
                                    onClick={() => {
                                        if (navigator.share) {
                                            navigator.share({
                                                title: 'Join AnimalKart',
                                                text: `Sign up using my referral link and let's grow together!`,
                                                url: `${window.location.origin}${window.location.pathname}?referral_code=${referralCode}`,
                                            }).catch(console.error);
                                        } else {
                                            handleCopy();
                                        }
                                    }}
                                    className="h-11 px-6 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-bold text-sm transition-all flex items-center gap-2 shadow-md shadow-teal-500/10"
                                >
                                    <Share2 size={16} />
                                    Share
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Registration Form Card */}
                    <div className="w-full max-w-md lg:shrink-0 animate-in fade-in slide-in-from-right-8 duration-1000 delay-200">
                        <div className="bg-white/10 backdrop-blur-xl rounded-[2.5rem] p-10 shadow-2xl border border-white/20 relative overflow-hidden">
                            {/* Decorative element */}
                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-full blur-3xl -mr-16 -mt-16 opacity-50" />

                            <div className="relative z-10">
                                <h2 className="text-2xl font-black text-white mb-2 tracking-tight">Get Started</h2>
                                <p className="text-white/70 font-medium text-sm mb-8">Join the community of 10k+ investors</p>

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="relative group">
                                        <div className={`absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none transition-colors ${errors.mobile ? 'text-red-400' : 'text-white group-focus-within:text-emerald-400'}`}>
                                            <span className="text-sm font-bold pt-0.5 border-r border-white/20 pr-3 uppercase tracking-widest">+91</span>
                                        </div>
                                        <input
                                            type="tel"
                                            name="mobile"
                                            placeholder="Mobile Number *"
                                            className={`w-full h-14 pl-20 pr-6 rounded-2xl border ${errors.mobile ? 'border-red-400/50 bg-red-500/10' : 'border-white/10 bg-white/5 focus:border-emerald-500/50 focus:bg-white/15'} outline-none transition-all font-medium text-white placeholder:text-white/40 placeholder:font-semibold`}
                                            value={formData.mobile}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            maxLength={10}
                                            required
                                        />
                                        {errors.mobile && <p className="text-[11px] text-red-500 font-bold mt-1.5 ml-1 uppercase tracking-wider">{errors.mobile}</p>}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5 group">
                                            <input
                                                type="text"
                                                name="first_name"
                                                placeholder="First Name *"
                                                className={`w-full h-14 px-5 rounded-2xl border ${errors.first_name ? 'border-red-400/50 bg-red-500/10' : 'border-white/10 bg-white/5 focus:border-emerald-500/50 focus:bg-white/15'} outline-none transition-all font-medium text-white placeholder:text-white/40 placeholder:font-semibold`}
                                                value={formData.first_name}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                required
                                            />
                                            {errors.first_name && <p className="text-[11px] text-red-500 font-bold ml-1 uppercase tracking-wider">{errors.first_name}</p>}
                                        </div>
                                        <div className="space-y-1.5 group">
                                            <input
                                                type="text"
                                                name="last_name"
                                                placeholder="Last Name *"
                                                className={`w-full h-14 px-5 rounded-2xl border ${errors.last_name ? 'border-red-400/50 bg-red-500/10' : 'border-white/10 bg-white/5 focus:border-emerald-500/50 focus:bg-white/15'} outline-none transition-all font-medium text-white placeholder:text-white/40 placeholder:font-semibold`}
                                                value={formData.last_name}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                required
                                            />
                                            {errors.last_name && <p className="text-[11px] text-red-500 font-bold ml-1 uppercase tracking-wider">{errors.last_name}</p>}
                                        </div>
                                    </div>

                                    <div className="group">
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="Email Address (Optional)"
                                            className={`w-full h-14 px-5 rounded-2xl border ${errors.email ? 'border-red-400/50 bg-red-500/10' : 'border-white/10 bg-white/5 focus:border-emerald-500/50 focus:bg-white/15'} outline-none transition-all font-medium text-white placeholder:text-white/40 placeholder:font-semibold`}
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                        />
                                        {errors.email && <p className="text-[11px] text-red-500 font-bold mt-1.5 ml-1 uppercase tracking-wider">{errors.email}</p>}
                                    </div>

                                    <div className="bg-white/5 rounded-2xl p-4 border border-dashed border-white/20 flex items-center justify-center">
                                        <span className="text-sm font-black text-white uppercase tracking-widest">{referralCode}</span>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading || !isFormValid}
                                        className={`w-full h-16 rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 shadow-xl ${!isFormValid || loading ? 'bg-slate-100 text-slate-400 shadow-none grayscale cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-500/20 active:scale-[0.98]'}`}
                                    >
                                        {loading ? (
                                            <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <>
                                                <span>Join Now</span>
                                                <ChevronRight size={18} strokeWidth={3} />
                                            </>
                                        )}
                                    </button>

                                    <p className="text-center text-[10px] text-white/50 font-bold leading-relaxed px-4">
                                        By joining, you agree to our <Link to="/privacy-policy" className="text-white underline underline-offset-2 hover:text-emerald-400 transition-colors">Privacy Policy</Link>.
                                    </p>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Sections: How It Works */}
            <section className="py-24 max-w-[1400px] mx-auto px-6">
                <div className="text-center mb-16">
                    <h2 className="text-3xl lg:text-4xl font-black text-slate-900 mb-4 tracking-tight">How it works</h2>
                    <p className="text-slate-500 font-bold uppercase tracking-[0.2em] text-xs">Unlock your coins in 3 simple steps</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-24 relative">
                    {/* Connecting arrows for desktop */}
                    <div className="hidden md:block absolute top-[60px] left-[30%] w-[10%] border-t-2 border-dashed border-slate-200" />
                    <div className="hidden md:block absolute top-[60px] left-[60%] w-[10%] border-t-2 border-dashed border-slate-200" />

                    {[
                        { icon: Users, title: 'Invite Friends', desc: 'Share your unique referral link with your circle.', color: 'bg-indigo-50 text-indigo-500 ring-indigo-500/20' },
                        { icon: ShieldCheck, title: 'Friend Purchases', desc: 'Your friends join and make their first purchase.', color: 'bg-emerald-50 text-emerald-500 ring-emerald-500/20' },
                        { icon: Gift, title: 'Earn Coins', desc: 'Gain bonus coins instantly after your friend\'s first purchase.', color: 'bg-amber-50 text-amber-500 ring-amber-500/20' },
                    ].map((step, i) => (
                        <div key={i} className="flex flex-col items-center text-center group">
                            <div className={`w-28 h-28 rounded-[2.5rem] flex items-center justify-center mb-8 ring-8 transition-all duration-500 group-hover:scale-110 shadow-sm ${step.color}`}>
                                <step.icon size={40} strokeWidth={1.5} />
                            </div>
                            <h3 className="text-xl font-black text-slate-900 mb-3 tracking-tight">{step.title}</h3>
                            <p className="text-slate-500 font-medium leading-relaxed max-w-[240px]">
                                {step.desc}
                            </p>
                        </div>
                    ))}
                </div>
            </section>


            <Snackbar
                message={snackbar.show ? snackbar.message : null}
                type={snackbar.show ? snackbar.type : null}
                onClose={() => setSnackbar({ ...snackbar, show: false })}
            />

            <style>{`
                @keyframes slow-zoom {
                    0% { transform: scale(1.05); }
                    50% { transform: scale(1); }
                    100% { transform: scale(1.05); }
                }
                .animate-slow-zoom {
                    animation: slow-zoom 20s infinite ease-in-out;
                }
                /* Handle Autofill transparency and text color */
                input:-webkit-autofill,
                input:-webkit-autofill:hover, 
                input:-webkit-autofill:focus, 
                input:-webkit-autofill:active {
                    -webkit-box-shadow: 0 0 0 30px rgba(255, 255, 255, 0.05) inset !important;
                    -webkit-text-fill-color: white !important;
                    transition: background-color 5000s ease-in-out 0s;
                }
                /* Ensure focus states are crisp on transparent bgs */
                input:focus {
                    background-color: rgba(255, 255, 255, 0.1) !important;
                }
            `}</style>
        </div>
    );
};

export default ReferralLandingPage;

